import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Card,
  CardBody,
  Col,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Label,
  Input,
  FormFeedback,
  Alert,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { DebounceInput } from "react-debounce-input";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { FieldArray, Formik, Form } from "formik";
import * as Yup from "yup";
import reactCSS from "reactcss";

import default_icon from "../../assets/images/upload.webp";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import {
  addCategory,
  getCategories,
  getCategory,
  updateCategory,
  deleteCategory,
  uploadCategoryIcon,
  toggleCategoryModalAction,
} from "../../store/services/categories/actions";
import { SketchPicker } from "react-color";

const CategoriesPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const {
    // categories
    categories,
    loadingCategories,
    category,
    // add
    addCategoryError,
    addCategoryLoading,
    addCategorySuccess,
    // update
    updateCategoryLoading,
    updateCategorySuccess,
    uploadingCategoryIcon,
    // get
    getCategorySuccess,
  } = useSelector((state) => state.Categories);

  document.title = `${t("Categories.Categories")} | ${t(
    process.env.REACT_APP_HEADING
  )}`;

  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);

  const toggleCategoryModal = () => {
    if (!isCategoryModalOpen) {
      dispatch(toggleCategoryModalAction());
      setBackgroundColor("#ff912e");
      setTitleColor("#ff912e");
      setSubTitleColor("#ff912e");
    }
    setIsCategoryModalOpen(!isCategoryModalOpen);
  };
  const [isCategoryDeleteModalOpen, setIsCategoryDeleteModalOpen] =
    useState(false);

  const toggleCategoryDeleteModal = () => {
    setIsCategoryDeleteModalOpen(!isCategoryDeleteModalOpen);
  };

  const [request, setRequest] = useState({
    limit: 10,
    page: 1,
    sortBy: "sequenceNo",
    sortDirection: "ASC",
  });

  const [totalRows, setTotalRows] = useState(0);

  const columns = [
    {
      name: (
        <span className="font-weight-bold fs-13">
          {t("Fields.Sequence No")}
        </span>
      ),
      selector: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <div className="flex-grow-1">{row.sequenceNo || "-"}</div>
        </div>
      ),
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">{t("Fields.Title")}</span>,
      selector: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <div className="flex-shrink-0">
            <img
              src={row.icon?.url}
              alt=""
              className="avatar-xs rounded-circle"
            />
          </div>
          <div className="flex-grow-1">
            {i18n.language === "ar" ? row.arabicTitle : row.title}
          </div>
        </div>
      ),
      sortable: true,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Fields.Sub Title")}</span>
      ),
      selector: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <div className="flex-grow-1">
            {i18n.language === "ar"
              ? row.subArabicTitle || "-"
              : row.subTitle || "-"}
          </div>
        </div>
      ),
      sortable: true,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Common.Created At")}</span>
      ),
      selector: (row) => new Date(row.createdAt).toLocaleDateString(),
      sortable: true,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Common.Action")}</span>
      ),
      sortable: false,
      cell: (row) => {
        return (
          <React.Fragment>
            <Button
              color="primary"
              className="btn-icon btn-sm"
              onClick={() => onEdit(row.id)}
            >
              <i className="ri-pencil-fill" />
            </Button>
            &nbsp;
            <Button
              color="danger"
              className="btn-icon btn-sm"
              onClick={() => onDelete(row.id)}
            >
              <i className="ri-delete-bin-5-line" />
            </Button>
          </React.Fragment>
        );
      },
    },
  ];

  const [rows, setRows] = useState([]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    let query = {
      limit: queryParams.has("limit") ? queryParams.get("limit") : 10,
      page: queryParams.has("page") ? queryParams.get("page") : 1,
      sortBy: queryParams.has("sortBy")
        ? queryParams.get("sortBy").split(":")[0]
        : "sequenceNo",
      sortDirection: queryParams.has("sortBy")
        ? queryParams.get("sortBy").split(":")[1]
        : "ASC",
      search: queryParams.has("search") ? queryParams.get("search") : "",
    };
    setDefaultSortAsc(query.sortDirection === "ASC");
    setDefaultSortFieldId(getSortByColumnId(query.sortBy));
    setRequest(query);
    dispatch(getCategories(query));
  }, []);

  useEffect(() => {
    const { limit, page, search, sortBy, sortDirection } = request;
    let query;
    if (page) query = `?page=${page}`;
    if (limit) query += `&limit=${limit}`;
    if (search && search !== "") {
      history.replace({
        search: `&search=${search}`,
      });
    }
    if (sortBy && sortDirection) query += `&sortBy=${sortBy}:${sortDirection}`;
    if (search) query += `&search=${search}`;
    history.push({ search: query });
  }, [request]);

  useEffect(() => {
    setRows(categories?.data);
    setTotalRows(categories.meta?.totalItems);
  }, [categories, loadingCategories]);

  const onChangeRowsPerPage = (pageSize) => {
    setRequest({ ...request, limit: pageSize });
    dispatch(getCategories({ ...request, limit: pageSize }));
  };

  const onChangePage = (page) => {
    setRequest({ ...request, page: page });
    dispatch(getCategories({ ...request, page: page }));
  };

  const onSearch = (text) => {
    setRequest({ ...request, search: text });
    dispatch(getCategories({ ...request, search: text }));
  };

  useEffect(() => {
    if (addCategorySuccess === true || updateCategorySuccess === true) {
      toggleCategoryModal();
      dispatch(getCategories(request));
    }
  }, [addCategorySuccess, updateCategorySuccess]);

  const getSortByColumnName = (id) => {
    let sortBy;
    if (i18n.language === "en") {
      switch (id) {
        case 1:
          sortBy = "sequenceNo";
          break;
        case 2:
          sortBy = "title";
          break;
        case 3:
          sortBy = "subTitle";
          break;
        case 4:
          sortBy = "createdAt";
          break;
        default:
          sortBy = "sequenceNo";
          break;
      }
    }

    if (i18n.language === "ar") {
      switch (id) {
        case 1:
          sortBy = "sequenceNo";
          break;
        case 2:
          sortBy = "arabicTitle";
          break;
        case 3:
          sortBy = "subArabicTitle";
          break;
        case 4:
          sortBy = "createdAt";
          break;
        default:
          sortBy = "sequenceNo";
          break;
      }
    }

    return sortBy;
  };

  const getSortByColumnId = (title) => {
    let sortBy;
    switch (title) {
      case "sequenceNo":
        sortBy = 1;
        break;
      case "title":
        sortBy = 2;
        break;
      case "arabicTitle":
        sortBy = 2;
        break;
      case "subTitle":
        sortBy = 3;
        break;
      case "subArabicTitle":
        sortBy = 3;
        break;
      default:
        sortBy = 1;
        break;
    }
    return sortBy;
  };

  const onSort = (column, sortDirection) => {
    const sortBy = getSortByColumnName(column.id);
    setRequest({
      ...request,
      sortBy: sortBy,
      sortDirection: sortDirection.toUpperCase(),
    });
    dispatch(
      getCategories({
        ...request,
        sortDirection: sortDirection.toUpperCase(),
        sortBy: sortBy,
      })
    );
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(1);
  const [defaultSortAsc, setDefaultSortAsc] = useState(true);

  /**
   * Formik handling
   */

  const validationSchema = Yup.object({
    sequenceNo: Yup.number().required(t("Validations.SequenceNo.Required")),
    title: Yup.string()
      .max(50, t("Validations.Common.Max", { length: 50 }))
      .required(t("Validations.Name.Required")),
    arabicTitle: Yup.string()
      .max(50, t("Validations.Common.Max", { length: 50 }))
      .required(t("Validations.ArabicName.Required")),
    subTitle: Yup.string()
      .max(50, t("Validations.Common.Max", { length: 50 }))
      .required(t("Validations.SubTitle.Required")),
    subArabicTitle: Yup.string()
      .max(50, t("", { length: 50 }))
      .required(t("Validations.ArabicSubTitle.Required")),
    color: Yup.string(),
    isFreeOfCost: Yup.bool(),
    plans: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().optional().nullable(),
          title: Yup.string()
            .max(50, t("Validations.Common.Max", { length: 50 }))
            .required(t("Validations.Title.Required")),
          arabicTitle: Yup.string()
            .max(50, t("Validations.Common.Max", { length: 50 }))
            .required(t("Validations.ArabicTitle.Required")),
          description: Yup.string()
            .max(255, t("Validations.Common.Max", { length: 255 }))
            .required(t("Validations.Description.Required")),
          arabicDescription: Yup.string()
            .max(255, t("Validations.Common.Max", { length: 255 }))
            .required(t("Validations.ArabicDescription.Required")),
          numberOfCalls: Yup.number().required(
            t("Validations.NumberOfCalls.Required")
          ),
          price: Yup.number().required(t("Validations.Price.Required")),
          discount: Yup.number(),
        })
      )
      .required("Must have at least one call"),
  });

  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  const onEdit = (id) => {
    setSelectedCategoryId(id);
    dispatch(getCategory(id));
    toggleCategoryModal();
  };

  const onDelete = (id) => {
    setSelectedCategoryId(id);
    toggleCategoryDeleteModal();
  };

  const onDeleteConfirmation = () => {
    dispatch(deleteCategory(selectedCategoryId));
    toggleCategoryDeleteModal();
    setTimeout(() => {
      dispatch(getCategories({ sortBy: "sequenceNo", sortDirection: "ASC" }));
      setSelectedCategoryId(null);
    }, 3000);
  };

  const onAdd = () => {
    setSelectedCategoryId(null);
    toggleCategoryModal();
  };

  const [iconError, setIconError] = useState(null);

  // handle change event of input file and validate it
  const onChangeAvatar = (event) => {
    const imageFile = event.target.files[0];
    const fileSizeKiloBytes = imageFile.size / 1024;

    if (!imageFile) {
      setIconError(t("mage.Please select image"));
      setTimeout(() => {
        setIconError(null);
      }, 3000);
      return false;
    }
    if (!imageFile.name.match(/\.(jpg|jpeg|png|gif|ico|icon)$/)) {
      setIconError(t("Please select valid image"));
      setTimeout(() => {
        setIconError(null);
      }, 3000);
      return false;
    }
    if (fileSizeKiloBytes > process.env.REACT_APP_MAX_ICON_SIZE) {
      setIconError(t("File exceeds maximum length"));
      setTimeout(() => {
        setIconError(null);
      }, 3000);
      return false;
    }
    dispatch(uploadCategoryIcon(imageFile));
  };

  // Color Picker

  const [displayBackgroundColorPicker, setDisplayBackgroundColorPicker] =
    useState();
  const [displayTitleColorPicker, setDisplayTitleColorPicker] = useState();
  const [displaySubTitleColorPicker, setDisplaySubTitleColorPicker] =
    useState();
  const [backgroundColor, setBackgroundColor] = useState("#ff912e");
  const [titleColor, setTitleColor] = useState("#ff912e");
  const [subTitleColor, setSubTitleColor] = useState("#ff912e");

  const handleBackgroundColorClick = () => {
    setDisplayBackgroundColorPicker(!displayBackgroundColorPicker);
  };

  const handleTitleColorClick = () => {
    setDisplayTitleColorPicker(!displayTitleColorPicker);
  };

  const handleSubTitleColorClick = () => {
    setDisplaySubTitleColorPicker(!displaySubTitleColorPicker);
  };

  const handleBackgroundColorClose = () => {
    setDisplayBackgroundColorPicker(false);
  };

  const handleTitleColorClose = () => {
    setDisplayTitleColorPicker(false);
  };

  const handleSubTitleColorClose = () => {
    setDisplaySubTitleColorPicker(false);
  };

  const handleBackgroundColorChange = (color) => {
    setBackgroundColor(color.hex);
  };

  const handleTitleColorChange = (color) => {
    setTitleColor(color.hex);
  };

  const handleSubTitleColorChange = (color) => {
    setSubTitleColor(color.hex);
  };

  const backgroundColorStyles = reactCSS({
    default: {
      color: {
        width: "50px",
        height: "20px",
        borderRadius: "2px",
        background: `${backgroundColor}`,
      },
      swatch: {
        padding: "5px",
        background: "#fff",
        borderRadius: "1px",
        boxShadow: "0 0 0 1px rgba(0,0,0,.1)",
        display: "inline-block",
        cursor: "pointer",
      },
      popover: {
        position: "absolute",
        zIndex: "2",
      },
      cover: {
        position: "fixed",
        top: "0px",
        right: "0px",
        bottom: "0px",
        left: "0px",
      },
    },
  });

  const titleColorStyles = reactCSS({
    default: {
      color: {
        width: "50px",
        height: "20px",
        borderRadius: "2px",
        background: `${titleColor}`,
      },
      swatch: {
        padding: "5px",
        background: "#fff",
        borderRadius: "1px",
        boxShadow: "0 0 0 1px rgba(0,0,0,.1)",
        display: "inline-block",
        cursor: "pointer",
      },
      popover: {
        position: "absolute",
        zIndex: "2",
      },
      cover: {
        position: "fixed",
        top: "0px",
        right: "0px",
        bottom: "0px",
        left: "0px",
      },
    },
  });

  const subTitleColorStyles = reactCSS({
    default: {
      color: {
        width: "50px",
        height: "20px",
        borderRadius: "2px",
        background: `${subTitleColor}`,
      },
      swatch: {
        padding: "5px",
        background: "#fff",
        borderRadius: "1px",
        boxShadow: "0 0 0 1px rgba(0,0,0,.1)",
        display: "inline-block",
        cursor: "pointer",
      },
      popover: {
        position: "absolute",
        zIndex: "2",
      },
      cover: {
        position: "fixed",
        top: "0px",
        right: "0px",
        bottom: "0px",
        left: "0px",
      },
    },
  });

  useEffect(() => {
    if (getCategorySuccess === true && category.backgroundColor) {
      setBackgroundColor(category.backgroundColor);
    }
    if (getCategorySuccess === true && category.titleColor) {
      setTitleColor(category.titleColor);
    }
    if (getCategorySuccess === true && category.subTitleColor) {
      setSubTitleColor(category.subTitleColor);
    }
  }, [getCategorySuccess]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("Categories.Categories")}
            pageTitle={t("Common.Users")}
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Row className="g-4 mb-3">
                    <Col className="col-sm-auto">
                      <div className="">
                        <div className="search-box ms-2">
                          <DebounceInput
                            className="form-control search"
                            placeholder={`${t("Common.Search")}...`}
                            minLength={2}
                            debounceTimeout={300}
                            onChange={(event) => onSearch(event.target.value)}
                            value={request.search}
                          />
                          <i className="ri-search-line search-icon"></i>
                        </div>
                      </div>
                    </Col>
                    <Col className="col-sm">
                      <div className="d-flex justify-content-sm-end">
                        <Button
                          color="success"
                          className="add-btn me-1"
                          id="create-btn"
                          onClick={onAdd}
                        >
                          <i className="ri-add-line align-bottom me-1"></i>{" "}
                          {t("Common.Add")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <DataTable
                    columns={columns}
                    data={rows}
                    progressPending={loadingCategories}
                    progressComponent={
                      <Spinner
                        size="sm"
                        className="flex-shrink-0 m-5"
                        role="status"
                      ></Spinner>
                    }
                    pagination
                    paginationServer
                    sortServer
                    onSort={onSort}
                    onChangeRowsPerPage={onChangeRowsPerPage}
                    onChangePage={onChangePage}
                    defaultSortFieldId={defaultSortFieldId}
                    defaultSortAsc={defaultSortAsc}
                    persistTableHead
                    paginationTotalRows={totalRows}
                    paginationComponentOptions={{
                      rowsPerPageText: t("Pagination.Rows per page"),
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      {/* Add Modal */}
      <Modal
        size="lg"
        isOpen={isCategoryModalOpen}
        toggle={() => {
          toggleCategoryModal();
        }}
        centered
      >
        <ModalHeader className="bg-light p-3">
          {selectedCategoryId
            ? t("Categories.Edit Category")
            : t("Categories.Add Category")}
          <Button
            type="button"
            onClick={() => {
              setIsCategoryModalOpen(false);
            }}
            className="btn-close"
            aria-label={t("Common.Close")}
          ></Button>
        </ModalHeader>
        <Formik
          enableReinitialize={true}
          initialValues={{
            title: category.title || "",
            arabicTitle: category.arabicTitle || "",
            subTitle: category.subTitle || "",
            subArabicTitle: category.subArabicTitle || "",
            sequenceNo: category.sequenceNo || "",
            isFreeOfCost: category.isFreeOfCost,
            plans: category.plans || [],
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            if (selectedCategoryId) {
              // update
              if (!category?.icon?.id) {
                setIconError("Required");
                setTimeout(() => {
                  setIconError(null);
                }, 3000);
                return;
              }
              dispatch(
                updateCategory({
                  ...values,
                  sequenceNo: Number(values.sequenceNo),
                  id: selectedCategoryId,
                  iconId: category.icon.id,
                  backgroundColor: backgroundColor,
                  titleColor: titleColor,
                  subTitleColor: subTitleColor,
                })
              );
            } else {
              if (!category?.icon?.id) {
                setIconError("Required");
                setTimeout(() => {
                  setIconError(null);
                }, 3000);
                return;
              }
              dispatch(
                addCategory({
                  ...values,
                  sequenceNo: Number(values.sequenceNo),
                  iconId: category.icon.id,
                  backgroundColor: backgroundColor,
                  titleColor: titleColor,
                  subTitleColor: subTitleColor,
                })
              );
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            setValues,
            handleChange,
            handleBlur,
            setFieldValue,
          }) => (
            <Form>
              <ModalBody>
                {addCategoryError && addCategoryError ? (
                  <Alert color="danger">
                    {" "}
                    {t(`Messages.Error.${addCategoryError}`)}{" "}
                  </Alert>
                ) : null}

                <div className="text-center">
                  {iconError && iconError ? (
                    <Alert color="danger">
                      {" "}
                      {t(`Messages.Error.${iconError}`)}{" "}
                    </Alert>
                  ) : null}
                  <div className="profile-user position-relative d-inline-block mx-auto  mb-4">
                    <img
                      src={category?.icon?.url || default_icon}
                      type="file"
                      className="rounded-circle avatar-lg img-thumbnail user-profile-image"
                      alt="user-profile"
                    />
                    <div className="avatar-xs p-0 rounded-circle profile-photo-edit">
                      <Input
                        id="profile-img-file-input"
                        type="file"
                        className="profile-img-file-input"
                        accept="image/*"
                        onChange={onChangeAvatar}
                      />
                      <Label
                        htmlFor="profile-img-file-input"
                        className="profile-photo-edit avatar-xs"
                      >
                        <span className="avatar-title rounded-circle bg-light text-body">
                          <i className="ri-camera-fill"></i>
                        </span>
                      </Label>
                    </div>
                  </div>
                  <Alert color="info">
                    {t(
                      "Messages.Information.Supported file types are jpg, png, gif, ico and icon"
                    )}
                    <br />
                    {t("Messages.Information.Recommended image resolution", {
                      resolution: " 122x124",
                    })}
                  </Alert>
                </div>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label htmlFor="sequenceNo" className="form-label">
                        {t("Fields.Sequence No")}
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        className="form-control"
                        id="sequenceNo"
                        placeholder={t(
                          "Messages.Information.Sequence no will be used for sorting"
                        )}
                        minLength={1}
                        maxLength={5}
                        value={values.sequenceNo || ""}
                        onChange={(e) => {
                          e.preventDefault();
                          const { value } = e.target;
                          const regex =
                            /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                          if (regex.test(value.toString())) {
                            setValues({
                              ...values,
                              sequenceNo: value,
                            });
                          } else {
                            setValues({
                              ...values,
                              sequenceNo: "",
                            });
                          }
                        }}
                        onBlur={handleBlur}
                        invalid={
                          touched.sequenceNo && errors.sequenceNo ? true : false
                        }
                      />
                      {touched.sequenceNo && errors.sequenceNo ? (
                        <FormFeedback type="invalid">
                          {errors.sequenceNo}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <div className="mb-3">
                      <Label htmlFor="title" className="form-label">
                        {t("Fields.Title")}
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        className="form-control"
                        id="title"
                        placeholder={t("Fields.Enter Title")}
                        value={values.title || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={touched.title && errors.title ? true : false}
                      />
                      {touched.title && errors.title ? (
                        <FormFeedback type="invalid">
                          {errors.title}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                  <Col>
                    <div className="mb-3">
                      <Label htmlFor="arabicTitle" className="form-label">
                        {t("Fields.Arabic Title")}
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        className="form-control"
                        id="arabicTitle"
                        placeholder={t("Fields.Enter Arabic Title")}
                        value={values.arabicTitle || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={
                          touched.arabicTitle && errors.arabicTitle
                            ? true
                            : false
                        }
                      />
                      {touched.arabicTitle && errors.arabicTitle ? (
                        <FormFeedback type="invalid">
                          {errors.arabicTitle}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <div className="mb-3">
                      <Label htmlFor="subTitle" className="form-label">
                        {t("Fields.Sub Title")}
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        className="form-control"
                        id="subTitle"
                        placeholder={t("Fields.Enter Sub Title")}
                        value={values.subTitle || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={
                          touched.subTitle && errors.subTitle ? true : false
                        }
                      />
                      {touched.subTitle && errors.subTitle ? (
                        <FormFeedback type="invalid">
                          {errors.subTitle}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                  <Col>
                    <div className="mb-3">
                      <Label htmlFor="subArabicTitle" className="form-label">
                        {t("Fields.Arabic Sub Title")}
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        className="form-control"
                        id="subArabicTitle"
                        placeholder={t("Fields.Enter Arabic Sub Title")}
                        value={values.subArabicTitle || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={
                          touched.subArabicTitle && errors.subArabicTitle
                            ? true
                            : false
                        }
                      />
                      {touched.subArabicTitle && errors.subArabicTitle ? (
                        <FormFeedback type="invalid">
                          {errors.subArabicTitle}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <div className="mb-3">
                      <Label htmlFor="color" className="form-label">
                        {t("Fields.Background Color")}
                      </Label>
                      <br />
                      <div>
                        <div
                          style={backgroundColorStyles.swatch}
                          onClick={handleBackgroundColorClick}
                        >
                          <div
                            style={{
                              ...backgroundColorStyles.color,
                              background: backgroundColor,
                            }}
                          />
                        </div>
                        {displayBackgroundColorPicker ? (
                          <div style={backgroundColorStyles.popover}>
                            <div
                              style={backgroundColorStyles.cover}
                              onClick={handleBackgroundColorClose}
                            />
                            <SketchPicker
                              color={backgroundColor}
                              onChange={handleBackgroundColorChange}
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <div className="mb-3">
                      <Label htmlFor="color" className="form-label">
                        {t("Fields.Title Text Color")}
                      </Label>
                      <div>
                        <div
                          style={titleColorStyles.swatch}
                          onClick={handleTitleColorClick}
                        >
                          <div
                            style={{
                              ...titleColorStyles.color,
                              background: titleColor,
                            }}
                          />
                        </div>
                        {displayTitleColorPicker ? (
                          <div style={titleColorStyles.popover}>
                            <div
                              style={titleColorStyles.cover}
                              onClick={handleTitleColorClose}
                            />
                            <SketchPicker
                              color={titleColor}
                              onChange={handleTitleColorChange}
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <div className="mb-3">
                      <Label htmlFor="color" className="form-label">
                        {t("Fields.Sub Title Text Color")}
                      </Label>
                      <br />
                      <div>
                        <div
                          style={subTitleColorStyles.swatch}
                          onClick={handleSubTitleColorClick}
                        >
                          <div
                            style={{
                              ...subTitleColorStyles.color,
                              background: subTitleColor,
                            }}
                          />
                        </div>
                        {displaySubTitleColorPicker ? (
                          <div style={subTitleColorStyles.popover}>
                            <div
                              style={subTitleColorStyles.cover}
                              onClick={handleSubTitleColorClose}
                            />
                            <SketchPicker
                              color={subTitleColor}
                              onChange={handleSubTitleColorChange}
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col
                    lg={6}
                    md={6}
                    style={{
                      alignContent: "flex-start",
                      display: "flex",
                    }}
                  >
                    <div className="form-check mb-4 mt-2">
                      <Input
                        className="form-check-input"
                        type="checkbox"
                        id="isFreeOfCost"
                        checked={values.isFreeOfCost}
                        onChange={(event) => {
                          const isChecked = event.target.checked;
                          setFieldValue("isFreeOfCost", isChecked);
                          if (!isChecked) {
                            setFieldValue("plans", [
                              {
                                numberOfCalls: "",
                                price: "",
                                discount: 0,
                                title: "",
                                arabicTitle: "",
                                description: "",
                                arabicDescription: "",
                                id: null,
                              },
                            ]);
                          } else {
                            setFieldValue("plans", []);
                          }
                        }}
                        onBlur={handleBlur}
                      />
                      <Label
                        className="form-check-label"
                        htmlFor="isFreeOfCost"
                      >
                        {t("Common.Make this category Free")}{" "}
                      </Label>
                    </div>
                  </Col>
                </Row>

                {values.isFreeOfCost === false && (
                  <FieldArray name="plans">
                    {({ remove, push }) => (
                      <div>
                        {values.plans.length > 0 &&
                          values.plans.map((plan, index) => (
                            <fieldset
                              key={index}
                              style={{
                                border: "solid 1px lightGrey",
                                marginBottom: 10,
                                padding: 10,
                                borderRadius: 5,
                              }}
                            >
                              <legend>
                                {" "}
                                {t("Common.Plan")} {index + 1}
                              </legend>
                              <Row>
                                <Col>
                                  <div className="mb-3">
                                    <Label
                                      htmlFor={`plans.${index}.title`}
                                      className="form-label"
                                    >
                                      {t("Fields.Title")}
                                      <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                      type="text"
                                      className="form-control"
                                      id={`plans.${index}.title`}
                                      name={`plans.${index}.title`}
                                      placeholder={t("Fields.Enter Title")}
                                      value={values.plans[index].title || ""}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      invalid={
                                        touched.plans &&
                                        touched.plans[index]?.title &&
                                        errors.plans &&
                                        errors.plans[index]?.title
                                          ? true
                                          : false
                                      }
                                    />
                                    {errors.plans &&
                                      errors.plans[index] &&
                                      errors.plans[index].title && (
                                        <FormFeedback type="invalid">
                                          {errors.plans[index].title}
                                        </FormFeedback>
                                      )}
                                  </div>
                                </Col>
                                <Col>
                                  <div className="mb-3">
                                    <Label
                                      htmlFor={`plans.${index}.arabicTitle`}
                                      className="form-label"
                                    >
                                      {t("Fields.Arabic Title")}
                                      <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                      type="text"
                                      className="form-control"
                                      id={`plans.${index}.arabicTitle`}
                                      name={`plans.${index}.arabicTitle`}
                                      placeholder={t(
                                        "Fields.Enter Arabic Title"
                                      )}
                                      value={
                                        values.plans[index].arabicTitle || ""
                                      }
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      invalid={
                                        touched.plans &&
                                        touched.plans[index]?.arabicTitle &&
                                        errors.plans &&
                                        errors.plans[index]?.arabicTitle
                                          ? true
                                          : false
                                      }
                                    />
                                    {errors.plans &&
                                      errors.plans[index] &&
                                      errors.plans[index].arabicTitle && (
                                        <FormFeedback type="invalid">
                                          {errors.plans[index].arabicTitle}
                                        </FormFeedback>
                                      )}
                                  </div>
                                </Col>
                              </Row>
                              <Row>
                                <Col>
                                  <div className="mb-3">
                                    <Label
                                      htmlFor={`plans.${index}.description`}
                                      className="form-label"
                                    >
                                      {t("Fields.Description")}
                                      <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                      type="textarea"
                                      className="form-control"
                                      id={`plans.${index}.description`}
                                      name={`plans.${index}.description`}
                                      placeholder={t(
                                        "Fields.Enter Description"
                                      )}
                                      value={
                                        values.plans[index].description || ""
                                      }
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      invalid={
                                        touched.plans &&
                                        touched.plans[index]?.description &&
                                        errors.plans &&
                                        errors.plans[index]?.description
                                          ? true
                                          : false
                                      }
                                    />
                                    {errors.plans &&
                                      errors.plans[index] &&
                                      errors.plans[index].description && (
                                        <FormFeedback type="invalid">
                                          {errors.plans[index].description}
                                        </FormFeedback>
                                      )}
                                  </div>
                                </Col>
                              </Row>
                              <Row>
                                <Col>
                                  <div className="mb-3">
                                    <Label
                                      htmlFor={`plans.${index}.arabicDescription`}
                                      className="form-label"
                                    >
                                      {t("Fields.Arabic Description")}
                                      <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                      type="textarea"
                                      className="form-control"
                                      id={`plans.${index}.arabicDescription`}
                                      name={`plans.${index}.arabicDescription`}
                                      placeholder={t(
                                        "Fields.Enter Arabic Description"
                                      )}
                                      value={
                                        values.plans[index].arabicDescription ||
                                        ""
                                      }
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      invalid={
                                        touched.plans &&
                                        touched.plans[index]
                                          ?.arabicDescription &&
                                        errors.plans &&
                                        errors.plans[index]?.arabicDescription
                                          ? true
                                          : false
                                      }
                                    />
                                    {errors.plans &&
                                      errors.plans[index] &&
                                      errors.plans[index].arabicDescription && (
                                        <FormFeedback type="invalid">
                                          {
                                            errors.plans[index]
                                              .arabicDescription
                                          }
                                        </FormFeedback>
                                      )}
                                  </div>
                                </Col>
                              </Row>
                              <Row>
                                <Col>
                                  <div className="mb-3">
                                    <Label
                                      htmlFor={`plans.${index}.numberOfCalls`}
                                      className="form-label"
                                    >
                                      {t("Fields.No of calls")}
                                      <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                      type="text"
                                      className="form-control"
                                      id={`plans.${index}.numberOfCalls`}
                                      name={`plans.${index}.numberOfCalls`}
                                      placeholder={t(
                                        "Fields.Enter no of calls"
                                      )}
                                      value={
                                        values.plans[index].numberOfCalls || ""
                                      }
                                      minLength={1}
                                      maxLength={10}
                                      onChange={(e) => {
                                        e.preventDefault();
                                        const { value } = e.target;
                                        const regex =
                                          /^\d{1,10}$|(?=^.{1,10}$)^\d+\.\d{0,2}$/;

                                        if (regex.test(value.toString())) {
                                          setFieldValue(
                                            `plans.${index}.numberOfCalls`,
                                            value
                                          );
                                        } else {
                                          if (value.toString().length === 0)
                                            setFieldValue(
                                              `plans.${index}.numberOfCalls`,
                                              ""
                                            );
                                        }
                                      }}
                                      onBlur={handleBlur}
                                      invalid={
                                        touched.plans &&
                                        touched.plans[index]?.numberOfCalls &&
                                        errors.plans &&
                                        errors.plans[index]?.numberOfCalls
                                          ? true
                                          : false
                                      }
                                    />
                                    {errors.plans &&
                                      errors.plans[index] &&
                                      errors.plans[index].numberOfCalls && (
                                        <FormFeedback type="invalid">
                                          {errors.plans[index].numberOfCalls}
                                        </FormFeedback>
                                      )}
                                  </div>
                                </Col>
                                <Col>
                                  <div className="mb-3">
                                    <Label
                                      htmlFor={`plans.${index}.price`}
                                      className="form-label"
                                    >
                                      {t("Fields.Price")}
                                      <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                      type="text"
                                      className="form-control"
                                      id={`plans.${index}.price`}
                                      name={`plans.${index}.price`}
                                      placeholder={t("Fields.Enter Price")}
                                      value={values.plans[index].price || ""}
                                      minLength={1}
                                      maxLength={10}
                                      onChange={(e) => {
                                        e.preventDefault();
                                        const { value } = e.target;
                                        const regex =
                                          /^\d{1,10}$|(?=^.{1,10}$)^\d+\.\d{0,2}$/;

                                        if (regex.test(value.toString())) {
                                          setFieldValue(
                                            `plans.${index}.price`,
                                            value
                                          );
                                        } else {
                                          if (value.toString().length === 0)
                                            setFieldValue(
                                              `plans.${index}.price`,
                                              ""
                                            );
                                        }
                                      }}
                                      onBlur={handleBlur}
                                      invalid={
                                        touched.plans &&
                                        touched.plans[index]?.price &&
                                        errors.plans &&
                                        errors.plans[index]?.price
                                          ? true
                                          : false
                                      }
                                    />
                                    {errors.plans &&
                                      errors.plans[index] &&
                                      errors.plans[index].price && (
                                        <FormFeedback type="invalid">
                                          {errors.plans[index].price}
                                        </FormFeedback>
                                      )}
                                  </div>
                                </Col>
                                <Col>
                                  <div className="mb-3">
                                    <Label
                                      htmlFor={`plans.${index}.discount`}
                                      className="form-label"
                                    >
                                      {t("Fields.Discount")} (
                                      {t("Common.Optional")})
                                    </Label>
                                    <Input
                                      type="number"
                                      className="form-control"
                                      id={`plans.${index}.discount`}
                                      name={`plans.${index}.discount`}
                                      placeholder={t("Fields.Enter Discount")}
                                      value={values.plans[index].discount || ""}
                                      min={0}
                                      max={100}
                                      onChange={(e) => {
                                        e.preventDefault();
                                        const { value } = e.target;
                                        const regex =
                                          /^\d{1,10}$|(?=^.{1,10}$)^\d+\.\d{0,2}$/;

                                        if (regex.test(value.toString())) {
                                          setFieldValue(
                                            `plans.${index}.discount`,
                                            value
                                          );
                                        } else {
                                          if (value.toString().length === 0)
                                            setFieldValue(
                                              `plans.${index}.discount`,
                                              ""
                                            );
                                        }
                                      }}
                                      onBlur={handleBlur}
                                      invalid={
                                        touched.plans &&
                                        touched.plans[index]?.discount &&
                                        errors.plans &&
                                        errors.plans[index]?.discount
                                          ? true
                                          : false
                                      }
                                    />
                                    {errors.plans &&
                                      errors.plans[index] &&
                                      errors.plans[index].discount && (
                                        <FormFeedback type="invalid">
                                          {errors.plans[index].discount}
                                        </FormFeedback>
                                      )}
                                  </div>
                                </Col>
                              </Row>
                              <button
                                className="btn btn-danger btn-sm"
                                type="button"
                                onClick={() => {
                                  remove(index);
                                  if (values.plans.length === 1) {
                                    setFieldValue("isFreeOfCost", true);
                                  }
                                }}
                              >
                                {t("Common.Delete")} {t("Common.Plan")}{" "}
                                {index + 1}
                              </button>
                            </fieldset>
                          ))}
                        <Row>
                          <Col>
                            <button
                              className="btn btn-success btn-sm"
                              type="button"
                              style={{ float: "right" }}
                              onClick={() =>
                                push({
                                  id: null,
                                  numberOfCalls: "",
                                  price: "",
                                  title: "",
                                  arabicTitle: "",
                                  description: "",
                                  arabicDescription: "",
                                  discount: 0,
                                })
                              }
                            >
                              {t("Common.Add New Plan")}
                            </button>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </FieldArray>
                )}
              </ModalBody>
              <ModalFooter>
                <div className="hstack gap-2 justify-content-end">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => setIsCategoryModalOpen(false)}
                  >
                    {t("Common.Close")}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success"
                    id="add-btn"
                    disabled={
                      addCategoryLoading ||
                      updateCategoryLoading ||
                      uploadingCategoryIcon
                    }
                  >
                    {addCategoryLoading || updateCategoryLoading ? (
                      <span className="d-flex align-items-center">
                        <span className="flex-grow-1 me-2">
                          {t("Common.Saving")}...
                        </span>
                        <Spinner
                          size="sm"
                          className="flex-shrink-0"
                          role="status"
                        >
                          {" "}
                          {t("Common.Saving")}
                          ...{" "}
                        </Spinner>
                      </span>
                    ) : (
                      <>{t("Common.Save")}</>
                    )}
                  </button>
                </div>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Remove Modal */}
      <Modal
        isOpen={isCategoryDeleteModalOpen}
        toggle={() => {
          toggleCategoryDeleteModal();
        }}
        centered
      >
        <div className="modal-header">
          <Button
            type="button"
            onClick={() => setIsCategoryDeleteModalOpen(false)}
            className="btn-close"
            aria-label="Close"
          >
            {" "}
          </Button>
        </div>
        <ModalBody>
          <div className="mt-2 text-center">
            <lord-icon
              src="https://cdn.lordicon.com/gsqxdxog.json"
              trigger="loop"
              colors="primary:#f7b84b,secondary:#f06548"
              style={{ width: "100px", height: "100px" }}
            ></lord-icon>
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t("Common.Are you Sure?")}</h4>
              <p className="text-muted mx-4 mb-0">
                {t("Common.Are you Sure You want to Remove this Record?")}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <button
              type="button"
              className="btn w-sm btn-light"
              onClick={() => {
                setSelectedCategoryId(null);
                toggleCategoryDeleteModal();
              }}
            >
              {t("Common.Close")}
            </button>
            <button
              type="button"
              className="btn w-sm btn-danger "
              id="delete-record"
              onClick={() => onDeleteConfirmation()}
            >
              {t("Common.Yes, Delete It!")}
            </button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default CategoriesPage;
